/**
 * The single HTTP client every API module goes through.
 *
 * Owns the cross-cutting concerns so no caller repeats them: bearer-token
 * headers, one-shot refresh-and-retry on expiry, `?unit=` scoping, query
 * building, and turning the backend's `{"detail": "..."}` error bodies into
 * a typed ApiError.
 */

// Requests go to /api on the page's own origin: the Vite dev proxy
// (VITE_API_BASE_URL in .env.development is the PROXY's target, not this)
// and nginx in production forward them to Django. VITE_API_BASE, when set,
// prefixes every call — for a deployment that serves the API elsewhere and
// allows that origin in CORS.
const BASE = import.meta.env.VITE_API_BASE ?? "";
const ACCESS_KEY = "ebmr.access";
const REFRESH_KEY = "ebmr.refresh";

export class ApiError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }

  /** 401/403 mean "sign in" / "not allowed", which callers render differently. */
  get isAuth() {
    return this.status === 401;
  }

  get isForbidden() {
    return this.status === 403;
  }
}

export const tokens = {
  get access() {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY);
  },
  set({ access, refresh }) {
    if (access) localStorage.setItem(ACCESS_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

/** Drops null/undefined so callers can pass optional params freely. */
export function buildQuery(params = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      search.append(key, value);
    }
  });
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

async function parseBody(response) {
  if (response.status === 204) return null;
  const type = response.headers.get("content-type") || "";
  if (type.includes("application/json")) return response.json();
  if (type.includes("text/")) return response.text();
  return response.blob();
}

/**
 * A text body worth showing, or null.
 *
 * The API answers errors as `{"detail": "..."}`, so a text body means the
 * response did not come from the API at all — a dev-server error page, a
 * proxy's 502, a gateway timeout. Those are HTML documents, and putting one
 * in a toast renders the markup as a wall of text where a sentence belongs.
 * Only a short, tag-free string is a message someone wrote to be read.
 */
function plainText(payload) {
  if (typeof payload !== "string") return null;
  const text = payload.trim();
  if (!text || text.length > 200) return null;
  return /<[a-z!/]/i.test(text) ? null : text;
}

/** What each status means when the response carried no usable message. */
const STATUS_MESSAGE = {
  400: "The server rejected this request",
  403: "Your role is not allowed to do this",
  404: "Not found",
  409: "That conflicts with something already saved — reload and try again",
  413: "That upload is too large",
  429: "Too many requests — wait a moment and try again",
  500: "Something went wrong on the server",
  502: "Cannot reach the server",
  503: "The server is unavailable — it may be restarting",
  504: "The server took too long to respond",
};

const statusMessage = (status) => STATUS_MESSAGE[status] ?? null;

async function refreshAccessToken() {
  const refresh = tokens.refresh;
  if (!refresh) return false;
  const response = await fetch(`${BASE}/api/token/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh }),
  });
  if (!response.ok) return false;
  const data = await response.json();
  tokens.set({ access: data.access, refresh: data.refresh });
  return true;
}

async function send(path, { method = "GET", body, params, headers, signal, raw } = {}) {
  const url = `${BASE}${path}${buildQuery(params)}`;
  const isFormData = body instanceof FormData;

  const call = () => {
    const auth = tokens.access ? { Authorization: `Bearer ${tokens.access}` } : {};
    return fetch(url, {
      method,
      signal,
      headers: {
        ...(isFormData ? {} : body !== undefined && { "Content-Type": "application/json" }),
        ...auth,
        ...headers,
      },
      body: isFormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
    });
  };

  // A fetch that never reaches the server rejects with the browser's own
  // "Failed to fetch", which names neither the cause nor the fix. An aborted
  // request is not a failure — the caller cancelled it, so it rethrows
  // untouched for `useApi` to ignore.
  let response;
  try {
    response = await call();
  } catch (err) {
    if (err?.name === "AbortError") throw err;
    throw new ApiError(
      "Cannot reach the server — check that the backend is running", 0, null);
  }

  // An expired access token is recoverable exactly once — refresh, retry,
  // and only then surface the 401 so the app can send the user to /login.
  if (response.status === 401 && tokens.refresh && (await refreshAccessToken())) {
    try {
      response = await call();
    } catch (err) {
      if (err?.name === "AbortError") throw err;
      throw new ApiError(
        "Cannot reach the server — check that the backend is running", 0, null);
    }
  }

  if (!response.ok) {
    const payload = await parseBody(response).catch(() => null);
    const detail =
      (payload && typeof payload === "object" && payload.detail) ||
      plainText(payload) ||
      statusMessage(response.status) ||
      response.statusText ||
      "Request failed";
    if (response.status === 401) {
      // The session is over (refresh failed or was never possible). Every
      // read is auth-gated server-side now, so this fires the moment a
      // stale session touches any endpoint — clear the tokens and tell the
      // app, which flips auth state to anonymous and routes to /login.
      tokens.clear();
      window.dispatchEvent(new Event("ebmr:session-expired"));
    }
    throw new ApiError(detail, response.status, payload);
  }

  return raw ? response : parseBody(response);
}

export const api = {
  get: (path, options) => send(path, { ...options, method: "GET" }),
  post: (path, body, options) => send(path, { ...options, method: "POST", body }),
  put: (path, body, options) => send(path, { ...options, method: "PUT", body }),
  del: (path, options) => send(path, { ...options, method: "DELETE" }),
  /** For file responses (Excel, CSV) that need headers, not a parsed body. */
  raw: (path, options) => send(path, { ...options, raw: true }),
};

/**
 * Downloads an export endpoint, honouring the server's filename.
 * Exports are auth-gated like everything else, so they cannot simply be
 * an <a href> — the bearer token has to ride along.
 */
export async function download(path, params) {
  const response = await api.raw(path, { params });
  const blob = await response.blob();
  const disposition = response.headers.get("content-disposition") || "";
  const match = disposition.match(/filename="?([^"]+)"?/);
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = match ? match[1] : path.split("/").pop();
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
